import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { School, User, Lock, Globe, Phone, MapPin, Plus, CheckCircle, List, Shield, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import RightSlideModal from '../../components/RightSlideModal';

export default function OnboardingPage() {
  const [activeTab, setActiveTab] = useState('onboard'); // 'onboard' or 'list'
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    schoolName: '',
    schoolCode: '',
    schoolAddress: '',
    adminContact: '',
    adminEmail: '',
    adminPassword: '',
    adminName: '',
    sessionYear: '2024-2025'
  });

  // Module state
  const [selectedSchoolForModules, setSelectedSchoolForModules] = useState(null);
  const [showModulesModal, setShowModulesModal] = useState(false);
  const [savingModules, setSavingModules] = useState(false);
  const [modulesState, setModulesState] = useState({
    classes: true,
    students: true,
    teachers: true,
    classTeachers: true,
    subjects: true,
    fees: true,
    attendance: true,
    exams: true,
    homework: true,
    announcements: true,
    birdAI: true
  });

  const moduleDetails = [
    { key: 'classes', label: 'Classes & Sections', desc: 'Manage class names, student levels, and classroom sections.' },
    { key: 'students', label: 'Student Management', desc: 'Register students, edit details, track parent contacts, and credentials.' },
    { key: 'teachers', label: 'Teacher Management', desc: 'Add/edit teachers, qualifications, and system privileges.' },
    { key: 'classTeachers', label: 'Class Teacher Assignment', desc: 'Assign primary class teachers to specific school classes.' },
    { key: 'subjects', label: 'Subjects', desc: 'Configure academic subjects per class and link curriculum.' },
    { key: 'fees', label: 'Fee Management', desc: 'Define billing structures, record quarterly payments, and check defaulters.' },
    { key: 'attendance', label: 'Attendance Tracking', desc: 'Record daily pupil presence and absence.' },
    { key: 'exams', label: 'Exams & Marks Entry', desc: 'Create test papers, record student grades, and track report cards.' },
    { key: 'homework', label: 'Homework Assignments', desc: 'Assign class work, outline tasks, and track submissions.' },
    { key: 'announcements', label: 'Announcements Board', desc: 'Publish official updates and news to students and teachers.' },
    { key: 'birdAI', label: 'BirdAI Assistant', desc: 'Provide AI-driven chat companion tools to users.' }
  ];

  useEffect(() => {
    if (activeTab === 'list') {
      fetchSchools();
    }
  }, [activeTab]);

  const fetchSchools = async () => {
    setLoading(true);
    try {
      const response = await api.get('/internal/schools');
      setSchools(response.data);
    } catch (error) {
      toast.error('Failed to fetch schools');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/internal/onboard-school', formData);
      toast.success('School onboarded successfully!');
      setFormData({
        schoolName: '', schoolCode: '', schoolAddress: '', adminContact: '',
        adminEmail: '', adminPassword: '', adminName: '', sessionYear: '2024-2025'
      });
      setActiveTab('list');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Onboarding failed');
    } finally {
      setLoading(false);
    }
  };

  const handleManageModules = async (school) => {
    setSelectedSchoolForModules(school);
    setShowModulesModal(true);
    try {
      const response = await api.get(`/internal/schools/${school.id}/modules`);
      setModulesState({
        classes: response.data.classes !== false,
        students: response.data.students !== false,
        teachers: response.data.teachers !== false,
        classTeachers: response.data.classTeachers !== false,
        subjects: response.data.subjects !== false,
        fees: response.data.fees !== false,
        attendance: response.data.attendance !== false,
        exams: response.data.exams !== false,
        homework: response.data.homework !== false,
        announcements: response.data.announcements !== false,
        birdAI: response.data.birdAI !== false,
      });
    } catch (error) {
      toast.error('Failed to load modules config');
    }
  };

  const handleSaveModules = async () => {
    setSavingModules(true);
    try {
      await api.post(`/internal/schools/${selectedSchoolForModules.id}/modules`, {
        enabledModules: modulesState
      });
      toast.success('School modules saved successfully!');
      setShowModulesModal(false);
    } catch (error) {
      toast.error('Failed to update modules configuration');
    } finally {
      setSavingModules(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6 md:p-10">
      <div className="max-w-5xl mx-auto">
        <header className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-outfit font-bold text-slate-900">Lumina AI Campus Internal</h1>
            <p className="text-slate-500 mt-1">Onboard new schools and manage internal systems</p>
          </div>
          <div className="flex bg-white p-1 rounded-xl shadow-sm border border-slate-200">
            <button 
              onClick={() => setActiveTab('onboard')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'onboard' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <Plus className="w-4 h-4" />
              New Onboarding
            </button>
            <button 
              onClick={() => setActiveTab('list')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'list' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-50'}`}
            >
              <List className="w-4 h-4" />
              School List
            </button>
          </div>
        </header>

        {activeTab === 'onboard' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* School Details Section */}
                <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm transition-all hover:shadow-md">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
                      <School className="w-5 h-5 text-indigo-600" />
                    </div>
                    <h2 className="text-lg font-outfit font-bold text-slate-800">School Information</h2>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 ml-1">School Name</label>
                      <div className="relative">
                        <School className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input 
                          type="text" 
                          required
                          value={formData.schoolName}
                          onChange={(e) => setFormData({...formData, schoolName: e.target.value})}
                          placeholder="International Public School"
                          className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-4 focus:ring-indigo-50 focus:border-indigo-500 outline-none transition-all"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 ml-1">Unique School Code</label>
                      <div className="relative">
                        <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input 
                          type="text" 
                          required
                          value={formData.schoolCode}
                          onChange={(e) => setFormData({...formData, schoolCode: e.target.value})}
                          placeholder="IPS101 (Used for login)"
                          className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-4 focus:ring-indigo-50 focus:border-indigo-500 outline-none transition-all uppercase"
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2 space-y-2">
                      <label className="text-sm font-medium text-slate-700 ml-1">Full Address</label>
                      <div className="relative">
                        <MapPin className="absolute left-4 top-4 w-4 h-4 text-slate-400" />
                        <textarea 
                          rows="2"
                          value={formData.schoolAddress}
                          onChange={(e) => setFormData({...formData, schoolAddress: e.target.value})}
                          placeholder="No. 123, Academic Street, Education Zone..."
                          className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-4 focus:ring-indigo-50 focus:border-indigo-500 outline-none transition-all"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Admin Details Section */}
                <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm transition-all hover:shadow-md">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                      <User className="w-5 h-5 text-emerald-600" />
                    </div>
                    <h2 className="text-lg font-outfit font-bold text-slate-800">Admin Account Credentials</h2>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 ml-1">Admin Full Name</label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input 
                          type="text" 
                          required
                          value={formData.adminName}
                          onChange={(e) => setFormData({...formData, adminName: e.target.value})}
                          placeholder="Principal / System Admin Name"
                          className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-4 focus:ring-emerald-50 focus:border-emerald-500 outline-none transition-all"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 ml-1">Admin Email Port</label>
                      <div className="relative">
                        <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input 
                          type="email" 
                          required
                          value={formData.adminEmail}
                          onChange={(e) => setFormData({...formData, adminEmail: e.target.value})}
                          placeholder="admin@ips-school.com"
                          className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-4 focus:ring-emerald-50 focus:border-emerald-500 outline-none transition-all"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 ml-1">Login Password</label>
                      <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input 
                          type="password" 
                          required
                          value={formData.adminPassword}
                          onChange={(e) => setFormData({...formData, adminPassword: e.target.value})}
                          placeholder="••••••••"
                          className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-4 focus:ring-emerald-50 focus:border-emerald-500 outline-none transition-all"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 ml-1">Contact Number</label>
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input 
                          type="tel" 
                          required
                          value={formData.adminContact}
                          onChange={(e) => setFormData({...formData, adminContact: e.target.value})}
                          placeholder="+91 XXXXX XXXXX"
                          className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-4 focus:ring-emerald-50 focus:border-emerald-500 outline-none transition-all"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-700 ml-1">Primary Session Year</label>
                      <select 
                        value={formData.sessionYear}
                        onChange={(e) => setFormData({...formData, sessionYear: e.target.value})}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:ring-4 focus:ring-indigo-50 focus:border-indigo-500 outline-none transition-all"
                      >
                        <option value="2024-2025">2024-2025</option>
                        <option value="2025-2026">2025-2026</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button 
                    type="submit" 
                    disabled={loading}
                    className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-400 text-white px-10 py-4 rounded-2xl font-bold font-outfit shadow-lg shadow-indigo-200 transition-all active:scale-95 flex items-center gap-3 text-lg"
                  >
                    {loading ? (
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <CheckCircle className="w-6 h-6" />
                    )}
                    Complete Onboarding
                  </button>
                </div>
              </form>
            </div>

            <div className="space-y-6">
              <div className="bg-indigo-900 rounded-2xl p-6 text-white shadow-xl shadow-indigo-100">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                  <Lock className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-outfit font-bold mb-2">Onboarding Guard</h3>
                <p className="text-indigo-200 text-sm leading-relaxed mb-4">
                  Everything here is irreversible. Use caution when defining School Codes. A code cannot be changed once the school is registered.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 text-sm text-indigo-100">
                    <CheckCircle className="w-4 h-4 mt-0.5 text-indigo-400" />
                    Creates School Profile
                  </div>
                  <div className="flex items-start gap-3 text-sm text-indigo-100">
                    <CheckCircle className="w-4 h-4 mt-0.5 text-indigo-400" />
                    Initializes First Admin
                  </div>
                  <div className="flex items-start gap-3 text-sm text-indigo-100">
                    <CheckCircle className="w-4 h-4 mt-0.5 text-indigo-400" />
                    Sets Active Academic Year
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
                <h3 className="font-outfit font-bold text-slate-800 mb-4">Tips</h3>
                <ul className="space-y-4">
                  <li className="flex gap-3 text-sm text-slate-600 leading-relaxed">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" />
                    School code should be short & memorable (e.g., SPS, HWS).
                  </li>
                  <li className="flex gap-3 text-sm text-slate-600 leading-relaxed">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-1.5 shrink-0" />
                    The primary session year will be the system default after login.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
             {loading ? (
               <div className="p-20 flex justify-center">
                 <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin" />
               </div>
             ) : (
               <div className="overflow-x-auto">
                 <table className="w-full text-left border-collapse">
                   <thead className="bg-slate-50/50 border-b border-slate-100">
                     <tr>
                       <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider">School Info</th>
                       <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Code</th>
                       <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider">Created At</th>
                       <th className="px-6 py-4 text-xs font-bold text-slate-400 uppercase tracking-wider text-right">Actions</th>
                     </tr>
                   </thead>
                   <tbody className="divide-y divide-slate-50">
                     {schools.length > 0 ? schools.map((school) => (
                       <tr key={school.id} className="hover:bg-slate-50/50 transition-colors">
                         <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className="font-bold text-slate-800">{school.name}</span>
                              <span className="text-xs text-slate-500">{school.address || 'No address provided'}</span>
                            </div>
                         </td>
                         <td className="px-6 py-4">
                           <span className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-lg font-mono text-sm font-bold border border-indigo-100">
                             {school.schoolCode}
                           </span>
                         </td>
                         <td className="px-6 py-4 text-sm text-slate-500">
                           {new Date(school.createdAt).toLocaleDateString()}
                         </td>
                         <td className="px-6 py-4 text-right">
                           <button 
                             onClick={() => handleManageModules(school)}
                             className="text-indigo-600 hover:text-indigo-850 bg-indigo-50 hover:bg-indigo-100 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
                           >
                             Manage Modules
                           </button>
                         </td>
                       </tr>
                     )) : (
                       <tr>
                         <td colSpan="4" className="px-6 py-20 text-center text-slate-500 italic">No schools onboarded yet.</td>
                       </tr>
                     )}
                   </tbody>
                 </table>
               </div>
             )}
          </div>
        )}
      </div>

      {/* Modules Toggles Drawer Modal */}
      <RightSlideModal
        isOpen={showModulesModal}
        onClose={() => setShowModulesModal(false)}
        title={`Modules Configuration: ${selectedSchoolForModules?.name || ''}`}
        maxWidth="max-w-xl"
      >
        <div className="space-y-6">
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-start gap-3">
            <Shield className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Toggling modules here instantly adapts the sidebar options and navigation permissions for all administrators, teachers, and students associated with this school.
              </p>
            </div>
          </div>

          <div className="divide-y divide-slate-100 max-h-[50vh] overflow-y-auto pr-2 scrollbar-thin">
            {moduleDetails.map((mod) => (
              <div key={mod.key} className="py-4 flex items-center justify-between gap-4">
                <div>
                  <h4 className="text-sm font-semibold text-slate-800">{mod.label}</h4>
                  <p className="text-xs text-slate-400 mt-0.5">{mod.desc}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setModulesState(prev => ({ ...prev, [mod.key]: !prev[mod.key] }))}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    modulesState[mod.key] ? 'bg-indigo-600' : 'bg-slate-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      modulesState[mod.key] ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>

          <div className="flex gap-3 pt-4 border-t border-slate-100">
            <button
              onClick={handleSaveModules}
              disabled={savingModules}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full py-3 font-semibold shadow-sm transition-all flex items-center justify-center gap-2"
            >
              {savingModules ? 'Saving...' : 'Save Configuration'}
            </button>
            <button
              onClick={() => setShowModulesModal(false)}
              className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-full py-3 font-medium transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      </RightSlideModal>
    </div>
  );
}
